// hooks/useServiciosPorEmpresa.ts
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export type Fase = {
  id_fase: string;
  nombre_fase: string;
  duracion_minutos: number;
  requiere_atencion: boolean;
  orden: number;
};

export type DiaServicio = {
  id_servicio_dia: string;
  dia_semana: string;
  descripcion: string | null;
  activo: boolean;
};

export type HorarioServicio = {
  id: string;
  hora: string;
  activo: boolean;
};

export type CategoriaServicioResumen = {
  id_categoria_servicio: string;
  nombre: string;
  icono: string | null;
  imagen: string | null;
  orden: number;
};

export type Servicio = {
  id_servicio: string;
  id_empresa: string;
  servicio: string;
  descripcion: string | null;
  precio: number | null;
  precio_aplica: string | null;
  cliente_min: number;
  cliente_max: number;
  capacidad_concurrente: number;
  duracion_minutos: number;
  permite_extension: boolean;
  permite_grupos: boolean;
  servicio_publico: boolean;
  requiere_trabajador: boolean;
  requiere_deposito: boolean;
  porcentaje_deposito: number | null;
  activo: boolean;
  horario_concreto?: boolean;
  reserva_por_horas?: boolean;
  horas_min?: number | null;
  horas_max?: number | null;
  reservable_online?: boolean;
  fecha_creacion: string;
  fecha_edicion: string | null;
  usuario_creacion: string | null;
  usuario_edicion: string | null;
  id_categoria_servicio: string | null;
  icono: string | null;
  orden: number;
  categoria_servicio: CategoriaServicioResumen | null;
  fases_servicio: Fase[] | null;
  dias_servicio: DiaServicio[] | null;
  horarios_servicio: HorarioServicio[] | null;
  tiene_citas?: boolean; // 👈
};

function safeParseJson<T>(value: unknown): T | null {
  if (value == null) return null;
  if (typeof value === 'object') return value as T;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function useServiciosPorEmpresa(
  idEmpresa: string,
  activos: boolean = true,
  soloPublicos: boolean = false
) {
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServicios = useCallback(async () => {
    if (!idEmpresa) {
      setServicios([]);
      return;
    }

    setLoading(true);
    setError(null);

    let query = supabase
      .from('vista_servicios_detalle')
      .select('*')
      .eq('id_empresa', idEmpresa)
      .order('servicio', { ascending: true });

    if (activos) query = query.eq('activo', true);
    if (soloPublicos) query = query.eq('servicio_publico', true);

    const { data, error } = await query;

    if (error) {
      Sentry.captureException(error, {
        extra: { idEmpresa, activos, soloPublicos },
      });
      const msg = 'Error al cargar los servicios';
      setError(msg);
      toast.error(msg);
      setServicios([]);
      setLoading(false);
      return;
    }

    // ── Verificar qué servicios tienen citas ──────────────────
    const ids = ((data as any[]) || []).map((s) => s.id_servicio);
    let citasPorServicio: Record<string, number> = {};

    if (ids.length > 0) {
      const { data: citasData, error: errorCitas } = await supabase
        .from('citas')
        .select('id_servicio')
        .in('id_servicio', ids);

      if (errorCitas) {
        Sentry.captureException(errorCitas, { extra: { ids } });
      } else {
        citasPorServicio = (citasData || []).reduce(
          (acc: Record<string, number>, c: any) => {
            acc[c.id_servicio] = (acc[c.id_servicio] || 0) + 1;
            return acc;
          },
          {}
        );
      }
    }

    const normalized: Servicio[] = ((data as any[]) || []).map((s) => ({
      ...s,
      tiene_citas: (citasPorServicio[s.id_servicio] ?? 0) > 0,
      categoria_servicio: safeParseJson<CategoriaServicioResumen>(
        s.categoria_servicio
      ),
      fases_servicio: (safeParseJson<Fase[]>(s.fases_servicio) ??
        s.fases_servicio ??
        null) as Fase[] | null,
      dias_servicio: (safeParseJson<DiaServicio[]>(s.dias_servicio) ??
        s.dias_servicio ??
        null) as DiaServicio[] | null,
      horarios_servicio: (safeParseJson<HorarioServicio[]>(
        s.horarios_servicio
      ) ??
        s.horarios_servicio ??
        null) as HorarioServicio[] | null,
    }));

    setServicios(normalized);
    setLoading(false);
  }, [idEmpresa, activos, soloPublicos]);

  // ============================================================
  // Mover servicio de categoría
  // ============================================================
  const moverServicioCategoria = useCallback(
    async (
      idServicio: string,
      nuevaCategoriaId: string | null,
      usuarioId: string | null
    ) => {
      const { error } = await supabase
        .from('servicios')
        .update({
          id_categoria_servicio: nuevaCategoriaId,
          usuario_edicion: usuarioId,
        })
        .eq('id_servicio', idServicio);

      if (error) {
        Sentry.captureException(error, {
          extra: { idServicio, nuevaCategoriaId },
        });
        return { ok: false };
      }

      return { ok: true };
    },
    []
  );

  // ============================================================
  // Eliminar servicio (solo si no tiene citas)
  // ============================================================
  const eliminarServicio = useCallback(async (idServicio: string) => {
    const { count, error: errorCount } = await supabase
      .from('citas')
      .select('id_servicio', { count: 'exact', head: true })
      .eq('id_servicio', idServicio);

    if (errorCount) {
      Sentry.captureException(errorCount, { extra: { idServicio } });
      toast.error('Error al verificar las citas del servicio.');
      return { ok: false };
    }

    if (count && count > 0) {
      toast.error('No se puede eliminar un servicio con citas asociadas.');
      return { ok: false, tieneCitas: true };
    }

    // Eliminar asignaciones de trabajadores al servicio
    const { error: errorUsuarios } = await supabase
      .from('servicios_usuarios')
      .delete()
      .eq('id_servicio', idServicio);

    if (errorUsuarios) {
      Sentry.captureException(errorUsuarios, { extra: { idServicio } });
      toast.error('Error al eliminar las asignaciones de trabajadores.');
      return { ok: false };
    }

    // Eliminar días del servicio
    const { error: errorDias } = await supabase
      .from('servicio_dias')
      .delete()
      .eq('id_servicio', idServicio);

    if (errorDias) {
      Sentry.captureException(errorDias, { extra: { idServicio } });
      toast.error('Error al eliminar los días del servicio.');
      return { ok: false };
    }

    // Eliminar horarios del servicio
    const { error: errorHorarios } = await supabase
      .from('servicio_horarios')
      .delete()
      .eq('id_servicio', idServicio);

    if (errorHorarios) {
      Sentry.captureException(errorHorarios, { extra: { idServicio } });
      toast.error('Error al eliminar los horarios del servicio.');
      return { ok: false };
    }

    // Eliminar fases del servicio
    const { error: errorFases } = await supabase
      .from('fases_servicio')
      .delete()
      .eq('id_servicio', idServicio);

    if (errorFases) {
      Sentry.captureException(errorFases, { extra: { idServicio } });
      toast.error('Error al eliminar las fases del servicio.');
      return { ok: false };
    }

    // Eliminar el servicio
    const { error } = await supabase
      .from('servicios')
      .delete()
      .eq('id_servicio', idServicio);

    if (error) {
      Sentry.captureException(error, { extra: { idServicio } });
      toast.error('Error al eliminar el servicio.');
      return { ok: false };
    }

    toast.success('Servicio eliminado correctamente.');
    return { ok: true };
  }, []);

  useEffect(() => {
    fetchServicios();
  }, [fetchServicios]);

  return {
    servicios,
    loading,
    error,
    refetch: fetchServicios,
    moverServicioCategoria,
    eliminarServicio,
  };
}

/* ============================================================
   SERVICIOS POR USUARIO
============================================================ */

export type ServicioUsuario = {
  id_servicio: string;
  nombre_servicio: string;
  servicio: string;
  precio: number;
  precio_aplica?: string | null;
  duracion_minutos: number;
  servicio_publico: boolean;
  cliente_min: number;
  cliente_max: number;
  requiere_trabajador: boolean;
  reserva_por_horas?: boolean;
  horas_min?: number | null;
  horas_max?: number | null;
};

export function useServiciosPorUsuario(
  idUsuario: string,
  idEmpresa: string,
  servicioPublico?: boolean
) {
  const [servicios, setServicios] = useState<ServicioUsuario[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServicios = useCallback(async () => {
    if (!idUsuario || !idEmpresa) {
      setServicios([]);
      return;
    }

    setLoading(true);
    setError(null);

    let query = supabase
      .from('vista_servicios')
      .select(
        'id_servicio, servicio, precio, precio_aplica, duracion_minutos, servicio_publico, cliente_min, cliente_max, requiere_trabajador, reserva_por_horas, horas_min, horas_max'
      )
      .eq('id_usuario', idUsuario)
      .eq('id_empresa', idEmpresa)
      .order('servicio', { ascending: true });

    if (servicioPublico === true) query = query.eq('servicio_publico', true);
    else if (servicioPublico === false)
      query = query.eq('servicio_publico', false);

    const { data, error } = await query;

    if (error) {
      Sentry.captureException(error, {
        extra: { idUsuario, idEmpresa, servicioPublico },
      });
      const msg = 'Error al cargar servicios por usuario';
      setError(msg);
      toast.error(msg);
      setServicios([]);
      setLoading(false);
      return;
    }

    setServicios(
      ((data as any[]) || []).map((s) => ({
        id_servicio: s.id_servicio,
        nombre_servicio: s.servicio,
        servicio: s.servicio,
        precio: s.precio,
        precio_aplica: s.precio_aplica,
        duracion_minutos: s.duracion_minutos,
        servicio_publico: s.servicio_publico,
        cliente_min: s.cliente_min ?? 1,
        cliente_max: s.cliente_max ?? 1,
        requiere_trabajador: s.requiere_trabajador,
        reserva_por_horas: s.reserva_por_horas ?? false,
        horas_min: s.horas_min,
        horas_max: s.horas_max,
      }))
    );

    setLoading(false);
  }, [idUsuario, idEmpresa, servicioPublico]);

  useEffect(() => {
    fetchServicios();
  }, [fetchServicios]);

  return { servicios, loading, error };
}
